import React, { useEffect, useState } from 'react';
import BoltIcon from '@mui/icons-material/Bolt';
import { useQuery, useReactiveVar, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Box, Card } from '@mui/material';
import {
  Loader,
  Typography,
  Button,
  CircularProgress,
  TucarTheme,
  IconButton,
  Chip,
} from '@tucar/pit-stop';
import { getDriverStore } from '../../../../graphql/store';
import GET_CHARGING_SESSIONS from '../../../../graphql/querys/map/getChargingSessions';
import STOP_CHARGING_SESSION from '../../../../graphql/mutations/map/stopChargingSession';
import autito from '../../../../../public/images/cars/autoPromedio.png';
import AlertBanner from './alertBanner';
import StationInfoCard from './stationInfoCard';
import { getChipColorSessionStatus, translateSessionStatus } from '../auxFunctions';
import EmptyView from '../../../../components/extras/EmptyView';

const ChargingView = () => {
  const { rent } = useReactiveVar(getDriverStore);
  const carId = rent?.carId;
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  const { loading, data, error } = useQuery(GET_CHARGING_SESSIONS.query, {
    ...GET_CHARGING_SESSIONS.policies,
    variables: {
      active: true,
      plate: carId,
      limit: 1,
    },
    skip: !carId,
  });

  const [stopCharging, { loading: stopLoading, data: stopData, error: stopError }] = useMutation(
    STOP_CHARGING_SESSION.mutation,
    STOP_CHARGING_SESSION.policies,
  );

  const activeSession = data?.getChargingSessions?.instances?.[0];

  useEffect(() => {
    if (stopLoading) return;

    if (stopError) {
      setErrorMessage(`No se pudo detener la carga: ${stopError.message}`);
      return;
    }

    if (stopData) {
      navigate('/');
    }
  }, [stopLoading, stopData, stopError, navigate]);

  const handleStopCharge = () => {
    // eslint-disable-next-line no-underscore-dangle
    const id = activeSession?._id;
    if (!id) return;

    stopCharging({
      variables: {
        sessionId: id,
      },
    });
  };

  if (loading)
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Loader size={32} pointRadio="4px" />
      </Box>
    );

  if (error)
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <AlertBanner
          message={`Error al cargar la sesión de carga: ${error.message}`}
          onClose={() => setErrorMessage('')}
        />
      </Box>
    );

  if (!activeSession) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100dvh',
          padding: '10px 74px',
          boxSizing: 'border-box',
        }}
      >
        <IconButton
          onClick={() => navigate('/app/v2/chargers')}
          style={{ position: 'absolute', top: '20px', left: '20px' }}
          iconProps={{ name: 'leftArrowSpecial' }}
        />
        <EmptyView />
      </Box>
    );
  }
  const sessionColor = getChipColorSessionStatus(activeSession.status);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        padding: '10px 74px',
        boxSizing: 'border-box',
        overflowY: 'auto',
      }}
    >
      {errorMessage && <AlertBanner message={errorMessage} onClose={() => setErrorMessage('')} />}
      <IconButton
        onClick={() => navigate('/app/v2/chargers')}
        style={{ position: 'absolute', top: '20px', left: '20px' }}
        iconProps={{ name: 'leftArrowSpecial' }}
      />
      <Typography variant="h1" fontSize={16} color={TucarTheme.colors.gray700}>
        Cargando
      </Typography>
      <CircularProgress
        onClick={() => {}}
        photoSrc={autito}
        loadingMode={activeSession.active}
        value={activeSession.finalSoc ? activeSession.finalSoc : '-'}
        size="230px"
      />
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          width: '100%',
          padding: '10px',
        }}
      >
        <Typography fontSize={13} color={TucarTheme.colors.gray700}>
          Carga suministrada
        </Typography>
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <BoltIcon sx={{ marginRight: '5px' }} />
          <Typography color={TucarTheme.colors.gray700} fontSize={24}>
            {activeSession.powerDelivered ? `${activeSession.powerDelivered} kWh` : '0.0 kWh'}
          </Typography>
        </Box>
      </Card>
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          width: '100%',
          padding: '10px',
        }}
      >
        <Typography fontSize={13} color={TucarTheme.colors.gray700}>
          Estado de la sesión
        </Typography>
        <Chip
          label={translateSessionStatus(activeSession.status)}
          variant="light"
          disabled
          color={sessionColor[0]}
          backgroundColor={sessionColor[1]}
        />
      </Card>
      {activeSession.connectorId && <StationInfoCard connectorId={activeSession.connectorId} />}
      <Button onClick={handleStopCharge} loading={stopLoading} style={{ marginTop: '30px' }}>
        Detener Carga
      </Button>
    </Box>
  );
};

export default ChargingView;
